/* eslint-disable no-param-reassign */

// node modules
import React from 'react';
import { gql } from '@apollo/client';
import capitalize from 'lodash/capitalize';
import { toast } from 'react-toastify';

// app modules: components
import Link from 'components/Link';

// app modules: services
import route from 'services/route';

// app modules: constants
import {
  CONTENT_TYPES,
  USER_COLLECTION_ADD_ITEM_TO_START,
  USER_COLLECTION_ADD_ITEM_TO_SUCCESS,
  USER_COLLECTION_ADD_ITEM_TO_ERROR,
} from 'constants/index';

const MUTATION = {
  ADD_ITEM_TO_COLLECTION: gql`
    mutation AddItemToCollection($id: Int, $itemId: String, $itemType: String) {
      AddItemToCollection(id: $id, itemId: $itemId, itemType: $itemType) {
        id
        item_id
        item_type
        cover_image {
          book {
            isbn
            image_name
            image_permission
          }
          primary_image {
            credit
            crops {
              sizes {
                id
                src
                w
                h
              }
              type
            }
            graphictype
            height
            id
            src
            type
            width
          }
          instagram_url
        }
      }
    }
  `,
};

/**
 * @param  {String} itemId
 * @param  {String} itemType
 * @return {String}
 */
export function getItemName(itemId, itemType) {
  switch (itemType) {
    case CONTENT_TYPES.PERSON:
      return 'author';

    case CONTENT_TYPES.BOOK:
      return 'book';

    default:
      if (route.contentType(itemId) === 'recipe') {
        return 'recipe';
      }
      return 'item';
  }
}

function handleError({ dispatch, errors }) {
  const {
    code,
    exception: { data },
  } = errors[0].extensions;
  const itemName = getItemName(data.item_id, data.item_type);

  dispatch({
    type: USER_COLLECTION_ADD_ITEM_TO_ERROR,
    payload: {
      errors,
    },
  });

  switch (code) {
    case 'BAD_USER_INPUT':
      toast.success(`${capitalize(itemName)} already in the collection.`);
      break;

    default:
      toast.error(`Could not add ${itemName} to collection.`);
      break;
  }
}

function handleSuccess({ dispatch, data, getState }) {
  const payload = data.AddItemToCollection;
  const itemName = getItemName(payload.item_id, payload.item_type);
  const collection = getState().user.collections[`collection-${payload.id}`] || {
    name: '',
    share_key: null,
  };

  dispatch({
    type: USER_COLLECTION_ADD_ITEM_TO_SUCCESS,
    payload,
  });

  toast.success(
    <p>
      {capitalize(itemName)} added to collection{' '}
      <Link
        className="link--energized"
        to={route.collection.view({ ...collection, id: payload.id })}
      >
        {collection.name}
      </Link>
    </p>,
  );
}

/**
 * @param {Object} params
 * @param {String} params.id
 * @param {String} params.itemId
 * @param {String} params.itemType
 */
export default function addItemToCollection(params) {
  const contentType = ['recipe', 'reference', 'section'];
  params.itemType = contentType.includes(params.itemType) ? 'content' : params.itemType;

  return async (dispatch, getState, { client }) => {
    dispatch({
      type: USER_COLLECTION_ADD_ITEM_TO_START,
      payload: params,
    });

    try {
      const { data, errors } = await client.mutate({
        mutation: MUTATION.ADD_ITEM_TO_COLLECTION,
        variables: params,
      });

      if (errors && errors.length > 0) {
        handleError({ dispatch, errors: errors[0].graphQLErrors, getState });
      } else {
        handleSuccess({ dispatch, data, getState });
      }
    } catch (error) {
      if (error.graphQLErrors && error.graphQLErrors.length) {
        handleError({ dispatch, errors: error.graphQLErrors });
      } else {
        console.error('Error adding to collection', error);
      }
    }
  };
}
